import { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { TourButton } from '@/components/ui/tour-button';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { ImportStatusIndicator } from '@/components/import/import-status-indicator';
import {
  Home,
  Package,
  ArrowRightLeft,
  Search,
  Settings,
  HelpCircle,
  Menu,
  X,
  ChevronDown,
  PlusCircle,
  TrendingDown,
  RefreshCw,
  Sparkles,
  MapPin,
  FolderTree,
  Wrench,
  Trash2,
  Upload,
  FileText,
  Sliders,
  BarChart3,
  Shield,
  ClipboardCheck,
  CheckSquare,
  Users,
  Calendar,
  LayoutDashboard,
  BookOpen,
} from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { GlobalSearch } from './global-search';
import { Breadcrumb } from './breadcrumb';
import { useAuditBadges } from '@/hooks/use-audit-badges';
import { useDemoMode } from '@/hooks/use-demo-mode';
import { useApplyFeatureModeForRoute } from '@/hooks/use-feature-mode';
import { useFilteredNavigation } from '@/hooks/use-permissions';

import { AssetDetailSheet } from '@/components/assets/AssetDetailSheet';
import { useAssetDetailSheet } from '@/hooks/use-asset-detail-sheet';
import { useImportAssetsModal } from '@/contexts/ImportAssetsModalContext';
import { useTour } from '@/contexts/TourContext';

interface NavItem {
  title: string;
  href?: string;
  icon: React.ComponentType<{ className?: string }>;
  children?: NavItem[];
  badge?: {
    count: number;
    variant?: 'default' | 'critical';
  };
}

const navigationItems: NavItem[] = [
  {
    title: 'Dashboard',
    href: '/dashboard',
    icon: LayoutDashboard,
  },
  {
    title: 'Asset Records',
    href: '/assets',
    icon: Package,
  },
  {
    title: 'Transactions',
    icon: TrendingDown,
    children: [
      {
        title: 'Additions',
        href: '/transactions/additions',
        icon: PlusCircle,
      },
      {
        title: 'Depreciation',
        href: '/transactions/depreciation',
        icon: TrendingDown,
      },
      {
        title: 'Revaluation',
        href: '/transactions/revaluation',
        icon: RefreshCw,
      },
    ],
  },
  {
    title: 'Audits',
    icon: ClipboardCheck,
    children: [
      {
        title: 'Create Audit',
        href: '/audits/create',
        icon: PlusCircle,
      },
      {
        title: 'Active Audits',
        href: '/audits/active',
        icon: ClipboardCheck,
      },
      {
        title: 'Scheduled Audits',
        href: '/audits/scheduled',
        icon: Calendar,
      },
      {
        title: 'Audit History',
        href: '/audits/history',
        icon: FileText,
      },
      {
        title: 'Exceptions',
        href: '/audits/exceptions',
        icon: Shield,
      },
    ],
  },
  {
    title: 'Compliance',
    icon: Shield,
    children: [
      {
        title: 'Dashboard',
        href: '/compliance',
        icon: BarChart3,
      },
      {
        title: 'Asset Compliance',
        href: '/compliance/assets',
        icon: Package,
      },
      {
        title: 'Regulations',
        href: '/compliance/regulations',
        icon: BookOpen,
      },
      {
        title: 'Schedule',
        href: '/compliance/schedule',
        icon: Calendar,
      },
    ],
  },
  {
    title: 'Work Items',
    href: '/tasks',
    icon: CheckSquare,
  },
  {
    title: 'Reports & Analytics',
    icon: BarChart3,
    children: [
      {
        title: 'Transaction History',
        href: '/enquiry',
        icon: Search,
      },
      {
        title: 'Audit Trail',
        href: '/audit',
        icon: FileText,
      },
    ],
  },
  {
    title: 'Administration',
    icon: Settings,
    children: [
      {
        title: 'Import Assets',
        href: '/administration/imports',
        icon: Upload,
      },
      {
        title: 'Locations',
        href: '/administration/locations',
        icon: MapPin,
      },
      {
        title: 'Categories',
        href: '/administration/categories',
        icon: FolderTree,
      },
      {
        title: 'Equipment',
        href: '/administration/equipment',
        icon: Wrench,
      },
      {
        title: 'Disposal Reasons',
        href: '/administration/disposal-reasons',
        icon: Trash2,
      },
    ],
  },
  {
    title: 'System Settings',
    icon: Sliders,
    children: [
      {
        title: 'Feature Settings',
        href: '/settings/features',
        icon: Sliders,
      },
      {
        title: 'Asset Options',
        href: '/settings/asset-options',
        icon: Settings,
      },
      {
        title: 'Period Management',
        href: '/settings/period-management',
        icon: Calendar,
      },
      {
        title: 'Team Permissions',
        href: '/administration/teams-permissions',
        icon: Users,
      },
      {
        title: 'Integration Log',
        href: '/settings/integration-log',
        icon: FileText,
      },
    ],
  },
  {
    title: 'Help & Support',
    href: '/help',
    icon: HelpCircle,
  },
];

export function AppLayout(): React.ReactElement {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { onStartTour } = useTour();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Load collapsed state from localStorage
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });
  // Track which menu item is currently expanded (null means all collapsed)
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const location = useLocation();
  const auditBadges = useAuditBadges();
  useDemoMode();
  const { assetId, isOpen, defaultTab, closeAssetDetail } = useAssetDetailSheet();

  // Filter navigation items based on user permissions
  // Note: Navigation visibility is based on user's team permissions,
  // NOT on whether the current screen is in demo mode
  const filteredNavItems = useFilteredNavigation(navigationItems);
  
  // Apply feature-specific mode based on current route
  // This syncs the module's configured mode from Feature Settings to ModeProvider
  // This affects data fetching for the current screen, but NOT navigation visibility
  useApplyFeatureModeForRoute();

  // Save collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  const toggleSidebarCollapse = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // Get navigation items with badges (using filtered items based on permissions)
  const navItemsWithBadges = filteredNavItems.map(item => {
    if (item.title === 'Audits & Compliance' && item.children) {
      return {
        ...item,
        children: item.children.map(child => {
          if (child.title === 'Active Audits') {
            return {
              ...child,
              badge: auditBadges.activeAudits > 0 ? {
                count: auditBadges.activeAudits,
                variant: 'default' as const,
              } : undefined,
            };
          }
          if (child.title === 'Scheduled Audits') {
            return {
              ...child,
              badge: auditBadges.scheduledAudits > 0 ? {
                count: auditBadges.scheduledAudits,
                variant: 'default' as const,
              } : undefined,
            };
          }
          if (child.title === 'Exceptions') {
            return {
              ...child,
              badge: auditBadges.openExceptions > 0 ? {
                count: auditBadges.openExceptions,
                variant: auditBadges.criticalExceptions > 0 ? 'critical' as const : 'default' as const,
              } : undefined,
            };
          }
          return child;
        }),
      };
    }
    return item;
  });

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm lg:hidden border-none cursor-default"
          onClick={() => setSidebarOpen(false)}
          onKeyDown={(e) => { if (e.key === 'Escape') { setSidebarOpen(false); } }}
          aria-label="Close sidebar"
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 transform border-r bg-card transition-all duration-300 ease-in-out lg:static lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
          sidebarCollapsed ? 'w-16' : 'w-64'
        )}
      >
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex h-16 items-center justify-between border-b px-4">
            <button
              onClick={toggleSidebarCollapse}
              className="flex items-center gap-2 overflow-hidden hover:opacity-80 transition-opacity cursor-pointer bg-transparent border-none p-0"
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <Package className="h-6 w-6 text-primary flex-shrink-0" />
              {!sidebarCollapsed && (
                <span className="text-lg font-semibold whitespace-nowrap">Access Assets</span>
              )}
            </button>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden flex-shrink-0"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 px-2 py-4">
            <ScrollAreaViewport className="h-full">
              <nav className="space-y-1">
                {navItemsWithBadges.map((item) => (
                  <NavItemComponent
                    key={item.title}
                    item={item}
                    currentPath={location.pathname}
                    collapsed={sidebarCollapsed}
                    onNavigate={() => setSidebarOpen(false)}
                    expandedItem={expandedItem}
                    setExpandedItem={setExpandedItem}
                  />
                ))}
              </nav>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" />
          </ScrollArea>

          {/* Footer with version */}
          <div className="border-t p-4">
            <div className="flex items-center justify-center">
              <p className="text-xs text-muted-foreground/60">
                v1.0.0
              </p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header className="flex h-16 items-center gap-4 border-b bg-card px-6">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden flex-shrink-0"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </Button>
            <div className="min-w-0 flex-1">
              <Breadcrumb />
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="w-[31.5rem] hidden sm:block">
              <GlobalSearch />
            </div>
            {onStartTour && <TourButton onClick={onStartTour} />}
            <ThemeToggle />
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>

      {/* Import Status Indicator */}
      <ImportStatusIndicator />

      {/* Global Asset Detail Sheet */}
      {assetId && (
        <AssetDetailSheet
          assetId={assetId}
          open={isOpen}
          onOpenChange={closeAssetDetail}
          defaultTab={defaultTab}
        />
      )}
    </div>
  );
}

function NavItemComponent({
  item,
  currentPath,
  collapsed,
  onNavigate,
  expandedItem,
  setExpandedItem,
}: Readonly<{
  item: NavItem;
  currentPath: string;
  collapsed: boolean;
  onNavigate: () => void;
  expandedItem: string | null;
  setExpandedItem: (title: string | null) => void;
}>) {
  const navigate = useNavigate();
  const { openModal } = useImportAssetsModal();
  // Use expandedItem state instead of local state
  const open = expandedItem === item.title;
  const isActive = item.href && currentPath === item.href;
  const Icon = item.icon;

  // Handle toggle with accordion behavior
  const handleToggle = () => {
    if (open) {
      // If this item is open, close it
      setExpandedItem(null);
    } else {
      // If this item is closed, open it (and close any other)
      setExpandedItem(item.title);
    }
  };

  const handleNavClick = (href: string | undefined, e: React.MouseEvent, title?: string) => {
    if (!href) return;
    
    // Special handling for Import Assets - open modal instead of navigating
    if (title === 'Import Assets' || href === '/administration/imports' || href === '/assets/import') {
      e.preventDefault();
      openModal();
      onNavigate();
      return;
    }
    
    // If clicking on the same page, force a refresh by navigating with replace
    if (currentPath === href) {
      e.preventDefault();
      navigate(href, { replace: true });
      // Force a page reload by navigating away and back
      navigate(href + '?refresh=' + Date.now(), { replace: true });
      setTimeout(() => {
        navigate(href, { replace: true });
      }, 0);
    }
    onNavigate();
  };

  // Collapsed state - show only icon
  if (collapsed) {
    if (item.children) {
      // For parent items with children, show icon with tooltip
      const hasActiveChild = item.children.some((child) => child.href && currentPath === child.href);
      return (
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            'w-full h-10',
            hasActiveChild && 'bg-accent text-accent-foreground'
          )}
          title={item.title}
        >
          <Icon className="h-5 w-5" />
        </Button>
      );
    }
    
    // For regular items, show as link
    if (item.href) {
      return (
        <Link to={item.href} onClick={(e) => handleNavClick(item.href, e, item.title)}>
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              'w-full h-10',
              isActive && 'bg-accent text-accent-foreground'
            )}
            title={item.title}
          >
            <Icon className="h-5 w-5" />
          </Button>
        </Link>
      );
    }
  }

  // Expanded state - show full menu
  if (item.children) {
    return (
      <Collapsible open={open} onOpenChange={handleToggle}>
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            className={cn(
              'w-full justify-start gap-3 font-normal h-10',
              item.children.some((child) => child.href && currentPath === child.href) &&
                'bg-accent text-accent-foreground'
            )}
          >
            <Icon className="h-4 w-4" />
            <span className="flex-1 text-left">{item.title}</span>
            <ChevronDown
              className={cn(
                'h-4 w-4 transition-transform',
                open && 'rotate-180'
              )}
            />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="ml-4 mt-1 space-y-1 border-l pl-4">
          {item.children.map((child) => {
            if (!child.href) return null;
            const ChildIcon = child.icon;
            const isChildActive = currentPath === child.href;
            return (
              <Link 
                key={child.href} 
                to={child.href} 
                onClick={(e) => handleNavClick(child.href, e, child.title)}
              >
                <Button
                  variant="ghost"
                  className={cn(
                    'w-full justify-start gap-3 font-normal h-9',
                    isChildActive && 'bg-accent text-accent-foreground'
                  )}
                >
                  <ChildIcon className="h-4 w-4" />
                  <span className="flex-1 text-left">{child.title}</span>
                  {child.badge && child.badge.count > 0 && (
                    <span
                      className={cn(
                        'flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-xs font-medium',
                        child.badge.variant === 'critical'
                          ? 'bg-red-500 text-white'
                          : 'bg-primary text-primary-foreground'
                      )}
                    >
                      {child.badge.count}
                    </span>
                  )}
                </Button>
              </Link>
            );
          })}
        </CollapsibleContent>
      </Collapsible>
    );
  }

  if (item.href) {
    return (
      <Link to={item.href} onClick={(e) => handleNavClick(item.href, e, item.title)}>
        <Button
          variant="ghost"
          className={cn(
            'w-full justify-start gap-3 font-normal h-10',
            isActive && 'bg-accent text-accent-foreground'
          )}
        >
          <Icon className="h-4 w-4" />
          <span className="flex-1 text-left">{item.title}</span>
          {item.badge && item.badge.count > 0 && (
            <span
              className={cn(
                'flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-xs font-medium',
                item.badge.variant === 'critical'
                  ? 'bg-red-500 text-white'
                  : 'bg-primary text-primary-foreground'
              )}
            >
              {item.badge.count}
            </span>
          )}
        </Button>
      </Link>
    );
  }

  return null;
}

function getPageInfo(pathname: string): { title: string; icon: React.ComponentType<{ className?: string }> } {
  const pages: Record<string, { title: string; icon: React.ComponentType<{ className?: string }> }> = {
    '/': { title: 'Dashboard', icon: LayoutDashboard },
    '/dashboard': { title: 'Dashboard', icon: LayoutDashboard },
    '/assets': { title: 'All Assets', icon: Package },
    '/transactions/additions': { title: 'Additions', icon: PlusCircle },
    '/transactions/depreciation': { title: 'Depreciation', icon: TrendingDown },
    '/transactions/revaluation': { title: 'Revaluation', icon: RefreshCw },
    '/enquiry': { title: 'Transaction History', icon: Search },
    '/audit': { title: 'Audit Trail', icon: FileText },
    '/tasks': { title: 'Work Items', icon: CheckSquare },
    '/checkinout': { title: 'Check-In / Check-Out', icon: ArrowRightLeft },
    '/checkinout/calendar': { title: 'Calendar View', icon: Calendar },
    '/checkinout/settings': { title: 'Check-In / Check-Out Settings', icon: Settings },
    '/compliance': { title: 'Compliance', icon: Shield },
    '/compliance/assets': { title: 'Asset Compliance', icon: Package },
    '/compliance/regulations': { title: 'Regulations', icon: BookOpen },
    '/compliance/schedule': { title: 'Schedule', icon: Calendar },
    '/audits/create': { title: 'Create Audit', icon: PlusCircle },
    '/audits/active': { title: 'Active Audits', icon: ClipboardCheck },
    '/audits/scheduled': { title: 'Scheduled Audits', icon: Calendar },
    '/audits/history': { title: 'Audit History', icon: FileText },
    '/audits/exceptions': { title: 'Exceptions', icon: Shield },
    '/administration': { title: 'Administration', icon: Settings },
    '/administration/imports': { title: 'Import Assets', icon: Upload },
    '/administration/locations': { title: 'Locations', icon: MapPin },
    '/administration/categories': { title: 'Categories', icon: FolderTree },
    '/administration/equipment': { title: 'Equipment', icon: Wrench },
    '/administration/disposal-reasons': { title: 'Disposal Reasons', icon: Trash2 },
    '/administration/teams-permissions': { title: 'Team Permissions', icon: Users },
    '/settings/features': { title: 'Feature Settings', icon: Sliders },
    '/settings/asset-options': { title: 'Asset Options', icon: Settings },
    '/settings/integration-log': { title: 'Integration Log', icon: FileText },
    '/settings/period-management': { title: 'Period Management', icon: Calendar },
    '/help': { title: 'Help & Support', icon: HelpCircle },
  };
  return pages[pathname] || { title: 'Access Assets', icon: Package };
}

function _getPageTitle(pathname: string): string {
  return getPageInfo(pathname).title;
}
